import React, {
  useCallback, useEffect, useMemo, useState, Fragment, ReactElement,
} from 'react';
import Table from '../../../components/table/table.component';
import TableBody from '../../../components/table-body/table-body.component';
import TableRow from '../../../components/table-row/table-row.component';
import TableCell from '../../../components/table-cell/table-cell.component';
import Tooltip from '../../../components/tooltip/tooltip.component';
import CheckAccess from '../../../components/check-access/check-access.component';
import ExpansionGroup from '../../../components/expansion-group/expansion-group.component';
import FlexContainer from '../../../components/flex-container/flex-container.component';
import ActionButton from '../../../components/action-button/action-button.component';
import FormFieldSelect from '../../../components/form-field-select/form-field-select.component';
import FormFieldText from '../../../components/form-field-text/form-field-text.component';
import FormFieldNumber from '../../../components/form-field-number/form-field-number.component';
import TableRowAddNewItem from '../../../components/table-row-add-new-item/table-row-add-new-item.component';
import FieldSeparatedBlock from '../../../components/field-separated-block/field-separated-block.component';
import MenuItemList from '../../../components/menu-item-list/menu-item-list.component';
import RemoveIcon from '../../../icons/remove-icon';
import StructureService from '../../../services/structure-service';
import FormService from '../../../services/form-service';
import { DEAL_BUILDING_DETAILS_ITEM_EDIT } from '../../../constants/entity/permissions';
import { DEAL_CAPITALIZATION_CATEGORY_SOURCES } from '../../../constants/entity/deal-capitalization-categories';
import { DEAL_CAPITALIZATION_GROUP_DEBT } from '../../../constants/entity/deal-capitalization-groups';
import PRICING_TYPE_FIXED from '../../../constants/entity/pricing-types';

const DealInvestmentReturns: ReactMemoComponent = React.memo((props: ReactComponentProps): ReactElement => {
  const COLUMNS_TOTAL: number = 3;

  const {
    isEditMode,
    isNewDeal,
    dealData,
    dealCapitalizationData,
    dealCapitalizationCategory,
    dealCapitalizationNewItems,
    dealCapitalizationGroupList,
    dealCapitalizationFormValues,
    dealInvestmentReturnsData,
    dealInvestmentReturnsNewItems,
    pricingTypeList,
    dealCapitalizationTypeList,
    onAddNewItem,
    onDeleteClick,
  } = props;

  const [dealInvestmentReturnsItems, setDealInvestmentReturnsItems]
    : [Record<string, DealInvestmentReturnsItem> | null, Function] = useState(null);

  const dealBuildingDetailsItemEditPermissions: string[] = useMemo((): string[] => [
    DEAL_BUILDING_DETAILS_ITEM_EDIT,
  ], []);

  const dealCapitalizationItemsTotal: number = useMemo((): number => (
    dealCapitalizationData && dealCapitalizationData.length ? dealCapitalizationData.reduce((
      itemsTotal: number, dealCapitalizationGroup: DealCapitalizationGroup,
    ): number => (
      itemsTotal + ((dealCapitalizationGroup.items && dealCapitalizationGroup.items.length) || 0)
    ), 0) : 0
  ), [dealCapitalizationData]);
  const dealInvestmentReturnsItemsTotal: number = useMemo((): number => (
    dealInvestmentReturnsData && dealInvestmentReturnsData.length ? dealInvestmentReturnsData.reduce((
      itemsTotal: number, dealInvestmentReturnsItem: DealInvestmentReturnsItem,
    ): number => (
      itemsTotal + (dealInvestmentReturnsItem.value ? 1 : 0)
    ), 0) : 0
  ), [dealInvestmentReturnsData]);

  const formPrefixCommon: string = 'dealData.dealCommonData';
  const formPrefixInvestmentReturns: string = 'dealData.dealInvestmentReturnsData';

  const addNewDealInvestmentReturnsItem: MouseEventHandlerFunction = useCallback((): void => {
    if (onAddNewItem) {
      onAddNewItem({
        id: StructureService.getNewItemId(dealInvestmentReturnsNewItems, dealInvestmentReturnsData),
        name: '',
        value: '',
      });
    }
  }, [dealInvestmentReturnsData, dealInvestmentReturnsNewItems]);

  const deleteNewDealInvestmentReturnClick: Function = useCallback((dealInvestmentReturnsItemId: number): void => {
    if (onDeleteClick) {
      onDeleteClick(dealInvestmentReturnsItemId);
    }

    if (dealInvestmentReturnsItems) {
      const newDealInvestmentReturnsItems: Record<string, DealInvestmentReturnsItem> = dealInvestmentReturnsItems;
      delete newDealInvestmentReturnsItems[dealInvestmentReturnsItemId];
      setDealInvestmentReturnsItems({ ...newDealInvestmentReturnsItems });
    }
  }, [dealInvestmentReturnsItems, dealInvestmentReturnsNewItems]);

  const getPricingText: Function = useCallback((dealCapitalizationItem: DealCapitalizationItem): string => {
    const pricingType: string = dealCapitalizationItem.pricingType
    && dealCapitalizationItem.pricingType.name !== PRICING_TYPE_FIXED
      ? `${dealCapitalizationItem.pricingType.title} + ` : '';

    return dealCapitalizationItem.pricing ? `${pricingType}${dealCapitalizationItem.pricing}` : '-';
  }, []);

  useEffect((): void => {
    if (dealInvestmentReturnsData) {
      const initialDealInvestmentReturnsItems: Record<string, DealInvestmentReturnsItem> = {};
      dealInvestmentReturnsData.forEach((dealInvestmentReturnsItem: DealInvestmentReturnsItem): void => {
        initialDealInvestmentReturnsItems[dealInvestmentReturnsItem.id] = { ...dealInvestmentReturnsItem };
      });
      setDealInvestmentReturnsItems(initialDealInvestmentReturnsItems);
    }
  }, [dealInvestmentReturnsData, isEditMode]);

  return (
    <CheckAccess permissions={dealBuildingDetailsItemEditPermissions}>
      <div>
        {
          ((dealData && (dealData.irrAmount || dealData.yieldToCostAmount))
            || !!dealCapitalizationItemsTotal || !!dealInvestmentReturnsItemsTotal || isEditMode || isNewDeal) && (
            <ExpansionGroup isExpanded isFixed headerComponent="Investment Returns">
              <Table>
                {
                  dealCapitalizationData && !!dealCapitalizationData.length && !isNewDeal && (
                    dealCapitalizationData.map((
                      dealCapitalizationGroup: DealCapitalizationGroup,
                    ): ReactElement | boolean => {
                      const dealCapitalizationGroupData: DealCapitalizationGroup = dealCapitalizationGroup
                        && dealCapitalizationGroupList && dealCapitalizationGroupList.find((
                          dealCapitalizationGroupItem: DealCapitalizationGroup,
                        ): boolean => (
                          dealCapitalizationGroupItem.title === dealCapitalizationGroup.name.trim()
                        ));
                      const dealCapitalizationSourcesItemList: DealCapitalizationItem[] = dealCapitalizationGroup.items
                      && !!dealCapitalizationGroup.items.length
                      && dealCapitalizationCategory === DEAL_CAPITALIZATION_CATEGORY_SOURCES
                        ? dealCapitalizationGroup.items : [];
                      const dealCapitalizationNewItemList: DealCapitalizationItem[] = (isEditMode || isNewDeal)
                      && dealCapitalizationNewItems[dealCapitalizationGroup.id]
                      && !!dealCapitalizationNewItems[dealCapitalizationGroup.id].length
                        ? dealCapitalizationNewItems[dealCapitalizationGroup.id] : [];
                      const renderedDealCapitalizationItemIds: number[] = [];
                      const formKeyDealCapitalizationGroup: string = FormService.getIndexedFormFieldKey(
                        dealCapitalizationGroup.id,
                      );

                      return ((dealCapitalizationGroupData
                        && dealCapitalizationGroupData.name === DEAL_CAPITALIZATION_GROUP_DEBT)) && (
                        <TableBody isPlain withZebraRows withHeaderColumn key={dealCapitalizationGroup.id}>
                          {
                            [...dealCapitalizationSourcesItemList, ...dealCapitalizationNewItemList]
                              .map((dealCapitalizationItem: DealCapitalizationItem): ReactElement | boolean => {
                                const formKeyDealCapitalizationItem: string = FormService.getIndexedFormFieldKey(
                                  dealCapitalizationItem.id,
                                );
                                const formPrefix: string = `dealData.dealCapitalizationData.`
                                  + `${dealCapitalizationCategory}[${formKeyDealCapitalizationGroup}].`
                                  + `items[${formKeyDealCapitalizationItem}]`;
                                const withFormValue: boolean = !!dealCapitalizationFormValues
                                  && !!dealCapitalizationFormValues[formKeyDealCapitalizationGroup]
                                  && !!dealCapitalizationFormValues[formKeyDealCapitalizationGroup]
                                    .items[formKeyDealCapitalizationItem]
                                const isNewItem: boolean = isNewDeal || (
                                  dealCapitalizationNewItems[dealCapitalizationGroup.id]
                                  && !!dealCapitalizationNewItems[dealCapitalizationGroup.id]
                                    .find((dealCapitalizationNewItem: DealCapitalizationItem): boolean => (
                                      dealCapitalizationNewItem.id === dealCapitalizationItem.id
                                    ))
                                );
                                const isRendered: boolean = renderedDealCapitalizationItemIds.includes(
                                  dealCapitalizationItem.id,
                                );

                                if (!isRendered) {
                                  renderedDealCapitalizationItemIds.push(dealCapitalizationItem.id);
                                }

                                return !isRendered && withFormValue && (
                                  <TableRow key={`${dealCapitalizationGroup.id}-${dealCapitalizationItem.id}`}>
                                    <TableCell>
                                      {
                                        isNewItem ? (
                                          <Fragment>
                                            <FormFieldText isHidden name={`${formPrefix}.id`} />
                                            <FormFieldText isDisabled withNoId name={`${formPrefix}.name`} />
                                          </Fragment>
                                        ) : dealCapitalizationItem.name
                                      }
                                    </TableCell>
                                    <TableCell>
                                      {
                                        (isEditMode || isNewDeal) ? (
                                          <FormFieldSelect
                                            withNoId
                                            withBlankElement
                                            isDisabled={dealCapitalizationTypeList
                                              && !dealCapitalizationTypeList.length}
                                            name={`${formPrefix}.type`}
                                            optionsTotal={dealCapitalizationTypeList
                                              && dealCapitalizationTypeList.length}
                                          >
                                            {
                                              MenuItemList({
                                                optionItemValueKey: 'name',
                                                optionList: dealCapitalizationGroup && dealCapitalizationTypeList
                                                  && dealCapitalizationTypeList.filter((
                                                    dealCapitalizationTypeItem: DealCapitalizationType,
                                                  ): boolean => (
                                                    dealCapitalizationTypeItem.group === dealCapitalizationGroup.type
                                                  )),
                                              })
                                            }
                                          </FormFieldSelect>
                                        ) : (dealCapitalizationItem.type && dealCapitalizationItem.type.title)
                                      }
                                    </TableCell>
                                    <TableCell isNumber>
                                      {
                                        (isEditMode || isNewDeal) ? (
                                          <FieldSeparatedBlock>
                                            <FormFieldNumber isPercent isFloat name={`${formPrefix}.pricing`} />
                                            <FormFieldSelect
                                              withNoId
                                              withBlankElement
                                              isDisabled={pricingTypeList && !pricingTypeList.length}
                                              name={`${formPrefix}.pricingType`}
                                              optionsTotal={pricingTypeList && pricingTypeList.length}
                                            >
                                              {
                                                MenuItemList({
                                                  optionItemValueKey: 'name',
                                                  optionList: dealCapitalizationGroup && pricingTypeList
                                                    && pricingTypeList.filter((
                                                      pricingTypeItem: PricingType,
                                                    ): boolean => (
                                                      pricingTypeItem.group === dealCapitalizationGroup.type
                                                    )),
                                                })
                                              }
                                            </FormFieldSelect>
                                          </FieldSeparatedBlock>
                                        ) : getPricingText(dealCapitalizationItem)
                                      }
                                    </TableCell>
                                  </TableRow>
                                );
                              })
                          }
                        </TableBody>
                      );
                    })
                  )
                }
                <TableBody isPlain withZebraRows withHeaderColumn>
                  <TableRow>
                    <TableCell isHeader colSpan={COLUMNS_TOTAL}>
                      Equity Returns
                    </TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell colSpan={COLUMNS_TOTAL - 1}>
                      IRR
                    </TableCell>
                    <TableCell isNumber>
                      {
                        (isEditMode || isNewDeal) ? (
                          <FormFieldNumber isPercent isFloat name={`${formPrefixCommon}.irrAmount`} />
                        ) : ((dealData && dealData.irrAmount) || '-')
                      }
                    </TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell colSpan={COLUMNS_TOTAL - 1}>
                      Yield To Cost
                    </TableCell>
                    <TableCell isNumber>
                      {
                        (isEditMode || isNewDeal) ? (
                          <FormFieldNumber isPercent isFloat name={`${formPrefixCommon}.yieldToCostAmount`} />
                        ) : ((dealData && dealData.yieldToCostAmount) || '-')
                      }
                    </TableCell>
                  </TableRow>
                </TableBody>
                {
                  ((dealInvestmentReturnsData && !!dealInvestmentReturnsData.length) || isEditMode || isNewDeal) && (
                    <TableBody isPlain withZebraRows withHeaderColumn>
                      <TableRow>
                        <TableCell isHeader colSpan={COLUMNS_TOTAL}>
                          Return Summary
                        </TableCell>
                      </TableRow>
                      {
                        [...((dealInvestmentReturnsItems && Object.values(dealInvestmentReturnsItems)) || []),
                          ...(dealInvestmentReturnsNewItems || [])]
                          .map((dealInvestmentReturnsItem: DealInvestmentReturnsItem): ReactElement => {
                            const isNewItem: boolean = dealInvestmentReturnsNewItems
                              && !!dealInvestmentReturnsNewItems.length && !!dealInvestmentReturnsNewItems.find((
                                dealInvestmentReturnsNewItem: DealInvestmentReturnsItem,
                              ): boolean => (
                                dealInvestmentReturnsNewItem.id === dealInvestmentReturnsItem.id
                              ));
                            const formKeyDealInvestmentReturnsItem: string = FormService.getIndexedFormFieldKey(
                              dealInvestmentReturnsItem.id,
                            );

                            return (
                              (
                                <TableRow isActive={isNewItem && !isNewDeal} key={dealInvestmentReturnsItem.id}>
                                  <TableCell colSpan={COLUMNS_TOTAL - 1}>
                                    {
                                      (isEditMode || isNewDeal) ? (
                                        <Fragment>
                                          <FormFieldText
                                            isHidden
                                            name={`${formPrefixInvestmentReturns}`
                                              + `[${formKeyDealInvestmentReturnsItem}].id`}
                                          />
                                          <FormFieldText
                                            name={`${formPrefixInvestmentReturns}`
                                              + `[${formKeyDealInvestmentReturnsItem}].name`}
                                            placeholder="Name"
                                          />
                                        </Fragment>
                                      ) : dealInvestmentReturnsItem.name
                                    }
                                  </TableCell>
                                  <TableCell isNumber>
                                    {
                                      (isEditMode || isNewDeal) ? (
                                        <FlexContainer>
                                          <FormFieldText
                                            name={`${formPrefixInvestmentReturns}`
                                              + `[${formKeyDealInvestmentReturnsItem}].value`}
                                            placeholder="Value"
                                          />
                                          <Tooltip title="Delete">
                                            <div>
                                              <ActionButton
                                                isInline
                                                withSmallPadding
                                                onClick={(): void => {
                                                  deleteNewDealInvestmentReturnClick(dealInvestmentReturnsItem.id);
                                                }}
                                              >
                                                <RemoveIcon />
                                              </ActionButton>
                                            </div>
                                          </Tooltip>
                                        </FlexContainer>
                                      ) : dealInvestmentReturnsItem.value
                                    }
                                  </TableCell>
                                </TableRow>
                              )
                            );
                          })
                      }
                      {
                        (isEditMode || isNewDeal) && (
                          <TableRowAddNewItem colSpan={COLUMNS_TOTAL} onClick={addNewDealInvestmentReturnsItem} />
                        )
                      }
                    </TableBody>
                  )
                }
              </Table>
            </ExpansionGroup>
          )
        }
      </div>
    </CheckAccess>
  );
});

export default DealInvestmentReturns;
